'use client'

import * as z from 'zod'
import { useState, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { Icons } from '@/components/shared/icons'

// Components
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { FormError } from '@/components/shared/forms/form-error'
import { FormSuccess } from '@/components/shared/forms/form-success'

// Utils
import { contact } from '@/actions/contact'

// Validation
import { ContactSchema } from '@/lib/validations/others'

// Types
import type { Locale } from '@/i18n-config'
import type { Dictionary } from '@/types/global'

export function ContactForm({
  lang,
  dictionary,
}: {
  lang: Locale
  dictionary: Dictionary
}) {
  const [error, setError] = useState<string | undefined>('')
  const [success, setSuccess] = useState<string | undefined>('')
  const [isPending, startTransition] = useTransition()

  const form = useForm<z.infer<typeof ContactSchema>>({
    resolver: zodResolver(ContactSchema),
    defaultValues: {
      name: '',
      phone: '',
      message: '',
    },
  })

  const onSubmit = (values: z.infer<typeof ContactSchema>) => {
    setError('')
    setSuccess('')

    startTransition(() => {
      contact(values)
        .then((data) => {
          if (data.error) {
            setError(data.error)
          }
          if (data.success) {
            form.reset()
            setSuccess(data.success)
          }
        })
        .catch((error) => {
          console.error('Error while sending contact:', error)
          setError('Something went wrong! Please try again.')
        })
    })
  }

  return (
    <div className="mx-auto max-w-7xl px-3 pb-24">
      <div className="lg:grid lg:grid-cols-2 lg:gap-6 space-y-6 md:space-y-0 ">
        <div className="space-y-3 pt-6">
          <h1 className="font-satoshi text-3xl font-bold">
            {dictionary.contact.title}
          </h1>
          <p className="text-sm">{dictionary.contact.description}</p>
          {/* <div className="space-y-2 py-3">
            <p className="text-sm">
              <span className="font-bold">Email:</span>{' '}
              <a href="mailto:test@test.com">test@test.com</a>
            </p>
            <p className="text-sm">
              <span className="font-bold">Phone:</span>{' '}
              <a href="tel:+1234567890">+123 456 7890</a>
            </p>
            <p className="text-sm">
              <span className="font-bold">Address:</span> 1234 Street, City,
              Country 12345
            </p>
            <p className="text-sm">
              <span className="font-bold">Office Hours:</span> Monday to Friday,
              9:00 AM to 5:00 PM
            </p>
          </div> */}
        </div>
        <div className="">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="w-full space-y-6 lg:pt-6"
            >
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{dictionary.contact.form.name}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          disabled={isPending}
                          placeholder={
                            dictionary.contact.form['name-placeholder']
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{dictionary.contact.form.phone}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          disabled={isPending}
                          placeholder={
                            dictionary.contact.form['phone-placeholder']
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {/* <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{dictionary.contact.form.email}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          disabled={isPending}
                          placeholder={
                            dictionary.contact.form['email-placeholder']
                          }
                          type="email"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                /> */}
                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{dictionary.contact.form.message}</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          disabled={isPending}
                          placeholder={dictionary.contact.form.message}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormError message={error} />
              <FormSuccess message={success} />
              <Button
                type="submit"
                disabled={isPending || !form.formState.isValid}
                className="w-full"
              >
                {isPending && (
                  <Icons.spinner className="mr-3 size-5 animate-spin" />
                )}
                <span>{dictionary.contact.form.submit}</span>
              </Button>
            </form>
          </Form>
        </div>
      </div>
    </div>
  )
}
